<template>
  <modal
    :name="name"
    :width="modalWidth"
    :height="height"
    :adaptive="adaptive"
    :scrollable="scrollable"
    :reset="reset"
    :clickToClose="clickToClose"
    :classes="modalClasses"
    @before-open="handleBeforeOpen"
    @opened="handleOpened"
    @before-close="handleBeforeClose"
    @closed="handleClosed"
  >
    <div class="themed-modal-container">
      <!-- Header -->
      <div v-if="showHeader" class="themed-modal-header">
        <div class="themed-modal-header-content">
          <h3>
            <slot name="header">{{ title }}</slot>
          </h3>
          <p v-if="$slots.subtitle || subtitle" class="themed-modal-subtitle">
            <slot name="subtitle">{{ subtitle }}</slot>
          </p>
        </div>
        <button
          v-if="showClose"
          class="themed-modal-close"
          @click="close"
          aria-label="Close modal"
          ref="closeButton"
        >
          <theme-icon icon="close" type="icons" />
        </button>
      </div>

      <!-- Body -->
      <div class="themed-modal-body">
        <slot></slot>
      </div>

      <!-- Footer -->
      <div v-if="$slots.footer || showFooter" class="themed-modal-footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </modal>
</template>

<script>
import ThemeIcon from './ThemeIcon.vue';

export default {
  name: 'ThemedModal',
  components: {
    ThemeIcon
  },
  props: {
    name: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg', 'xl', 'full'].includes(value)
    },
    width: {
      type: [String, Number],
      default: null
    },
    height: {
      type: [String, Number],
      default: 'auto'
    },
    adaptive: {
      type: Boolean,
      default: true
    },
    scrollable: {
      type: Boolean,
      default: true
    },
    reset: {
      type: Boolean,
      default: false
    },
    clickToClose: {
      type: Boolean,
      default: true
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: false
    },
    showClose: {
      type: Boolean,
      default: true
    },
    customClass: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      focusableElements: [],
      firstFocusableElement: null,
      lastFocusableElement: null
    }
  },
  computed: {
    modalWidth() {
      if (this.width) {
        return this.width;
      }
      
      const sizeMap = {
        sm: 400,
        md: 600,
        lg: 800,
        xl: 1000,
        full: '95%'
      };
      
      return sizeMap[this.size] || sizeMap.md;
    },
    modalClasses() {
      const baseClasses = `themed-modal themed-modal-${this.size}`;
      return this.customClass ? `${baseClasses} ${this.customClass}` : baseClasses;
    }
  },
  methods: {
    close() {
      this.$modal.hide(this.name);
    },
    handleBeforeOpen(event) {
      this.$emit('before-open', event);
    },
    handleOpened(event) {
      this.$emit('opened', event);
      this.setupFocusTrap();
      this.setupKeyboardNavigation();
      
      // Focus first focusable element or close button
      this.$nextTick(() => {
        if (this.firstFocusableElement) {
          this.firstFocusableElement.focus();
        } else if (this.$refs.closeButton) {
          this.$refs.closeButton.focus();
        }
      });
    },
    handleBeforeClose(event) {
      this.$emit('before-close', event);
      this.teardownKeyboardNavigation();
    },
    handleClosed(event) {
      this.$emit('closed', event);
    },
    setupFocusTrap() {
      // Get all focusable elements within the modal
      const modalElement = this.$el.querySelector('.themed-modal-container');
      if (!modalElement) return;

      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(', ');

      this.focusableElements = Array.from(
        modalElement.querySelectorAll(focusableSelectors)
      );

      if (this.focusableElements.length > 0) {
        this.firstFocusableElement = this.focusableElements[0];
        this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
      }
    },
    setupKeyboardNavigation() {
      document.addEventListener('keydown', this.handleKeyDown);
    },
    teardownKeyboardNavigation() {
      document.removeEventListener('keydown', this.handleKeyDown);
    },
    handleKeyDown(event) {
      // Close on Escape key
      if (event.key === 'Escape' && this.clickToClose) {
        this.close();
        return;
      }

      // Focus trap with Tab key
      if (event.key === 'Tab') {
        if (this.focusableElements.length === 0) {
          event.preventDefault();
          return;
        }

        if (event.shiftKey) {
          // Shift + Tab: move focus backwards
          if (document.activeElement === this.firstFocusableElement) {
            event.preventDefault();
            this.lastFocusableElement.focus();
          }
        } else {
          // Tab: move focus forwards
          if (document.activeElement === this.lastFocusableElement) {
            event.preventDefault();
            this.firstFocusableElement.focus();
          }
        }
      }
    }
  },
  beforeDestroy() {
    this.teardownKeyboardNavigation();
  }
}
</script>

<style scoped>
/* Component-specific styles are in modal.less */
/* This scoped style section ensures proper encapsulation */
</style>
